<template>
    <BaseDialog
        v-model:open="isOpen"
        layout="custom"
        width="default"
        :title="titles[activeTab]"
        :show-close="!isBusy"
        float-close
        :disable-close-on-backdrop-click="isBusy"
        :dialog-content-class="dialogContentClass">
        <div class="px-5 pt-5 md:px-6 md:pt-6">
            <h2 class="display-small-serif pr-10">{{ titles[activeTab] }}</h2>
        </div>
        <BaseTabs
            :model-value="activeTab"
            :tab-ids="tabIds"
            :labels="tabLabels"
            :disabled="isBusy"
            class="min-h-0 flex-1"
            list-class="mt-3 px-5 md:px-6"
            content-class="flex min-h-0 flex-1 flex-col"
            @update:model-value="selectTab">
            <template #content-directory>
                <OrganizationProfileDirectoryContent
                    hide-title
                    :has-collaboration-profile="hasCollaborationProfile"
                    :create-profile-url="createProfileUrl"
                    :invite-url="inviteUrl"
                    :source="directorySource"
                    @added="emit('changed')"
                    @removed="emit('changed')"
                    @close="isOpen = false"
                    @busy="isBusy = $event" />
            </template>
            <template #content-calendar>
                <div class="tab-body">
                    <BaseNotification v-if="calendarError" theme="error" :text="calendarError" @dismiss="calendarError = ''" />
                    <div v-if="calendarStatus === 'loading'" class="flex justify-center py-10">
                        <LoadingSpinner class="size-6" />
                    </div>
                    <div v-else-if="calendarStatus === 'error'" class="flex flex-col items-start gap-3">
                        <BaseNotification theme="error" hide-dismiss :text="t('connections.addPeopleDialog.calendarStatusError')" />
                        <BaseButton theme="secondary" size="sm" @click="checkCalendarStatus">{{ t("common.tryAgain") }}</BaseButton>
                    </div>
                    <CalendarPeoplePicker
                        v-else-if="calendarStatus === 'connected'"
                        :calendar-people-api-url="calendarPeopleApiUrl"
                        :bulk-connection-api-url="bulkConnectionApiUrl"
                        :footer-target="footerSelector"
                        @imported="handleCalendarImported"
                        @cancel="isOpen = false"
                        @busy="isBusy = $event"
                        @empty="calendarListEmpty = $event" />
                    <CalendarConnectStep
                        v-else
                        source="connections"
                        :intro="t('connections.addPeopleDialog.calendarIntro')"
                        :next-url="calendarReturnUrl()"
                        :footer-target="footerSelector"
                        @connected="handleCalendarConnected"
                        @error="calendarError = $event" />
                </div>
            </template>
            <template #content-bulk_upload>
                <div class="tab-body">
                    <CsvImportContent
                        :csv-template-url="csvTemplateUrl"
                        :csv-preview-url="csvPreviewUrl"
                        :csv-import-url="csvImportUrl"
                        :footer-target="footerSelector"
                        @completed="emit('csvImported', $event)"
                        @imported="isOpen = false"
                        @close="isOpen = false"
                        @busy="isBusy = $event" />
                </div>
            </template>
            <template #content-manual>
                <div class="tab-body">
                    <ManualAddPersonForm
                        :source="directorySource"
                        :public-profile-enabled="publicProfileEnabled"
                        :footer-target="footerSelector"
                        @saved="emit('changed')"
                        @added="isOpen = false"
                        @cancel="isOpen = false"
                        @busy="isBusy = $event" />
                </div>
            </template>
        </BaseTabs>
        <div :id="footerId" class="tab-footer">
            <BaseButton v-if="activeTab === 'calendar' && calendarStatus === 'disconnected'" theme="tertiary" @click="isOpen = false">{{ t("common.cancel") }}</BaseButton>
        </div>
    </BaseDialog>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { ProfileDirectorySource } from "~vue/api/generated/collab-profiles";
import type { CsvImportStatusResponse } from "~vue/api/generated/connections-csv";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import CalendarConnectStep from "~vue/components/CalendarIntegration/CalendarConnectStep.vue";
import { ADD_PEOPLE_TABS, type AddPeopleTab } from "~vue/Connections/addPeopleTabs";
import CalendarPeoplePicker from "~vue/Connections/CalendarPeoplePicker.vue";
import CsvImportContent from "~vue/Connections/CsvImportContent.vue";
import ManualAddPersonForm from "~vue/Connections/ManualAddPersonForm.vue";
import OrganizationProfileDirectoryContent from "~vue/Connections/OrganizationProfileDirectoryContent.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

type CsvImportResult = Pick<CsvImportStatusResponse, "created" | "updated" | "skipped" | "errors">;

const props = defineProps<{
    initialTab?: AddPeopleTab;
    publicProfileEnabled?: boolean;
    calendarImportEnabled?: boolean;
    hasCalendarIntegration?: boolean;
    csvImportEnabled?: boolean;
    hasCollaborationProfile: boolean;
    createProfileUrl: string;
    inviteUrl: string;
    directorySource: ProfileDirectorySource;
    calendarPeopleApiUrl: string;
    bulkConnectionApiUrl: string;
    csvTemplateUrl: string;
    csvPreviewUrl: string;
    csvImportUrl: string;
}>();
const emit = defineEmits<{
    changed: [];
    calendarImported: [result: { created: number; linked: number }];
    csvImported: [result: CsvImportResult];
}>();
const isOpen = defineModel<boolean>("open", { default: false });

const { t } = useI18n();
const footerId = useId();
const footerSelector = `#${footerId}`;

const tabIds = computed(() => {
    const enabled: Record<AddPeopleTab, boolean> = {
        directory: props.publicProfileEnabled,
        calendar: props.calendarImportEnabled,
        bulk_upload: props.csvImportEnabled,
        manual: true,
    };
    return ADD_PEOPLE_TABS.filter((tab) => enabled[tab]);
});
const tabLabels = computed<Record<AddPeopleTab, string>>(() => ({
    directory: t("connections.addPeopleDialog.tabs.directory"),
    calendar: t("connections.addPeopleDialog.tabs.calendar"),
    bulk_upload: t("connections.addPeopleDialog.tabs.bulkUpload"),
    manual: t("connections.addPeopleDialog.tabs.manual"),
}));
const titles = computed<Record<AddPeopleTab, string>>(() => ({
    directory: t("connections.addPeople.fromOrganization"),
    calendar: t("connections.addPeopleDialog.titles.calendar"),
    bulk_upload: t("connections.addPeopleDialog.titles.bulkUpload"),
    manual: t("connections.addPeople.addOneAtATime"),
}));

const activeTab = ref<AddPeopleTab>("manual");
const isBusy = ref(false);
const calendarError = ref("");
const calendarStatus = ref<"loading" | "connected" | "disconnected" | "error">("loading");
// The calendar connection check re-runs on every visit, so the dialog height follows the last
// resolved state rather than the live one — otherwise the box would collapse while checking and
// snap back. Connected is seeded from the page prop so a connected calendar opens at list height
// immediately; the picker's resolved list size then decides list vs. compact height.
const calendarConnected = ref(props.hasCalendarIntegration ?? false);
const calendarListEmpty = ref(false);
// People lists keep a fixed height so searching or selecting doesn't resize the dialog; Manual and
// Bulk upload share one height so toggling between them doesn't jump. A connected calendar with no
// candidates shares that compact height — the box only resizes when the list's content changes.
// The remaining 140↔200 resizes between form and list tabs glide instead of snapping.
const dialogContentClass = computed(() => {
    const base = "flex h-full flex-col motion-safe:transition-[height] motion-safe:duration-200 motion-safe:ease-out";
    if (activeTab.value === "directory" || (activeTab.value === "calendar" && calendarConnected.value && !calendarListEmpty.value)) {
        return `${base} md:h-200`;
    }
    return `${base} md:h-140`;
});
const {
    execute: fetchCalendarStatus,
    data: calendarStatusData,
    isFetching: isFetchingCalendarStatus,
    error: calendarStatusRequestError,
} = useFetch("/api/calendar_integration/status/", { immediate: false, updateDataOnError: false }).get().json<{ calendarIntegrationStatus: boolean }>();

watch(
    isOpen,
    (open) => {
        if (open) {
            activeTab.value = props.initialTab && tabIds.value.includes(props.initialTab) ? props.initialTab : tabIds.value[0];
            isBusy.value = false;
            calendarError.value = "";
        }
    },
    { immediate: true },
);

// The connection can change in another tab or popup while this page stays open, so check on every visit.
async function checkCalendarStatus() {
    calendarStatus.value = "loading";
    await fetchCalendarStatus();
    // A newer check superseded this one.
    if (isFetchingCalendarStatus.value) {
        return;
    }
    const connected = calendarStatusData.value?.calendarIntegrationStatus;
    // An HTTP error still resolves, so only a successful boolean answer may decide connected vs. not.
    if (calendarStatusRequestError.value || typeof connected !== "boolean") {
        calendarStatus.value = "error";
    } else {
        calendarStatus.value = connected ? "connected" : "disconnected";
        calendarConnected.value = connected;
    }
}
watch(
    () => isOpen.value && activeTab.value === "calendar",
    (shown) => shown && checkCalendarStatus(),
    { immediate: true },
);
// Each tab is a fresh flow, so opening Bulk upload starts the CSV funnel like the standalone dialog did.
watch(
    () => isOpen.value && activeTab.value === "bulk_upload",
    (shown) => shown && logUserInteraction(AmplitudeEventNames.CSV_IMPORT_DIALOG_OPENED),
    { immediate: true },
);

// Same-window OAuth returns here, so keep the user's filters and reopen this tab.
function calendarReturnUrl() {
    const params = new URLSearchParams(globalThis.location.search);
    params.set("add_people_tab", "calendar");
    return `${globalThis.location.pathname}?${params}`;
}

function selectTab(tab: string | undefined) {
    const next = tabIds.value.find((id) => id === tab);
    if (!next || next === activeTab.value) {
        return;
    }
    activeTab.value = next;
    logUserInteraction(AmplitudeEventNames.PEOPLE_ADD_TAB_SELECTED, { people_add_tab: next });
}

function handleCalendarConnected() {
    calendarError.value = "";
    calendarStatus.value = "connected";
    calendarConnected.value = true;
}

function handleCalendarImported(result: { created: number; linked: number }) {
    isOpen.value = false;
    emit("calendarImported", result);
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    .tab-body {
        @apply flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5 md:p-6;
    }
    .tab-footer {
        @apply flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border p-5 md:px-6 empty:hidden;
    }
}
</style>
